import { useState, useEffect, useRef } from "react";
import "./css/productmoddetail.css";
import { apiGet, apiPutFormData } from "../../../utils/apiClient";

const STATE_MAP = {
  대기: "PENDING",
  "판매 중": "ON_SALE",
  "판매 종료": "ENDED",
  품절: "SOLDOUT",
  "판매 제재": "REJECTED",
};

const ENUM_STATE_MAP = {
  PENDING: "대기",
  ON_SALE: "판매 중",
  ENDED: "판매 종료",
  SOLDOUT: "품절",
  REJECTED: "판매 제재",
};

const ProductModDetail = ({ product, onClose, onSave }) => {
  const [dragActive, setDragActive] = useState({
    store: false,
  });

  const removeImageFromList = (index) => {
    setAllImages((prev) => {
      const newImages = prev.filter((_, i) => i !== index);
      setSelectedImageIndex((cur) => {
        if (newImages.length === 0) return 0;
        if (cur >= newImages.length) return newImages.length - 1;
        return cur;
      });
      return newImages;
    });
  };

  const [formData, setFormData] = useState({
    productNm: "",
    categoryIdx: "",
    productPrice: "",
    productAmount: "",
    productState: "PENDING",
    isAdult: false,
    isStock: true,
    productDetailDescription: "",
  });

  const [categories, setCategories] = useState([]);

  // 통합된 이미지 리스트 (기존 + 새로운)
  const [allImages, setAllImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [descImgFiles, setDescImgFiles] = useState([]);
  const descEditorRef = useRef(null);
  const descImgInputRef = useRef(null);
  const savedRangeRef = useRef(null);

  // 판매 제재 상태 확인
  const isEditable = formData.productState !== "REJECTED";
  // 할인/네고 진행 중일 때 판매상태 변경 불가
  const isStatusLocked = product?.isSale || product?.isNego;

  const handleDrag = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive((prev) => ({ ...prev, [type]: true }));
    } else if (e.type === "dragleave") {
      setDragActive((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive((prev) => ({ ...prev, [type]: false }));

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleProductImageSelect(e.dataTransfer.files[0]);
    }
  };

  const handleProductImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleProductImageSelect(e.target.files[0]);
    }
  };

  const handleProductImageSelect = (file) => {
    // 최대 3개 제한 체크
    if (allImages.length >= 3) {
      alert("이미지는 최대 3개까지만 첨부 가능합니다.");
      return;
    }

    if (file && file.type.startsWith("image/")) {
      // 파일 크기 체크 (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        // 새로운 이미지를 allImages에 추가
        setAllImages((prev) => [
          ...prev,
          {
            type: "new",
            data: file,
            preview: reader.result,
            name: file.name,
          },
        ]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileLabelClick = (e) => {
    if (allImages.length >= 3) {
      e.preventDefault();
      alert("이미지는 최대 3개까지만 첨부 가능합니다. 삭제 후 추가해주세요.");
    }
  };

  useEffect(() => {
    loadCategories();

    if (product) {
      // productState 한글 -> Enum 매핑

      setFormData({
        productNm: product.productNm || "",
        categoryIdx: "",
        productPrice: product.productPrice || "",
        productAmount: product.productAmount || "",
        productState: STATE_MAP[product.productState] || product.productState,
        isAdult: product.isAdult || false,
        isStock: product.isStock !== undefined ? product.isStock : true,
      });

      // contentEditable 콘텐츠 초기 세팅은 ref가 준비된 후에 useEffect에서 처리

      // 기존 이미지를 allImages에 추가 (type: 'existing' 표시)
      if (product.imgs?.files && product.imgs.files.length > 0) {
        const existingImages = product.imgs.files.map((img) => {
          // originalName 또는 fileOriginNm 중 존재하는 것 사용
          const fileName = img.originalName || img.fileOriginNm || "";

          return {
            type: "existing",
            data: img,
            preview: img.fileUrl,
            name: fileName,
          };
        });
        setAllImages(existingImages);
        setSelectedImageIndex(0); // 첫 번째 이미지를 메인으로 설정
      } else {
        setAllImages([]);
        setSelectedImageIndex(0);
      }
    }
  }, [product]);

  useEffect(() => {
    if (categories.length > 0 && product?.categoryNm) {
      const matchedCategory = categories.find(
        (cat) => cat.categoryNm === product.categoryNm,
      );
      if (matchedCategory) {
        setFormData((prev) => ({
          ...prev,
          categoryIdx: matchedCategory.categoryIdx,
        }));
      }
    }
  }, [categories, product?.categoryNm]);

  useEffect(() => {
    if (
      product?.productDetailDescription !== undefined &&
      descEditorRef.current
    ) {
      descEditorRef.current.innerHTML = product.productDetailDescription || "";
    }
  }, [product]);

  const loadCategories = async () => {
    try {
      const response = await apiGet("/category");
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setCategories(data);
        }
      }
    } catch (err) {
      console.error("카테고리 조회 실패:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue =
      name === "isStock"
        ? value === "true"
        : name === "isAdult"
          ? value === "true"
          : value;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      return updated;
    });
  };

  const handleDescImageInsert = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const img = document.createElement("img");
    img.src = blobUrl;
    img.style.maxWidth = "100%";
    img.style.height = "auto";

    // 저장된 커서 위치 복원 (버튼 클릭 시 저장)
    const range =
      savedRangeRef.current ||
      (() => {
        const r = document.createRange();
        r.selectNodeContents(descEditorRef.current);
        r.collapse(false);
        return r;
      })();
    savedRangeRef.current = null;

    descEditorRef.current.focus();
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    range.deleteContents();
    range.insertNode(img);
    range.setStartAfter(img);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    setDescImgFiles((prev) => [...prev, { blobUrl, file }]);
  };

  // 파일 확장자로 MIME 타입 결정
  const getMimeTypeFromFileName = (fileName) => {
    if (!fileName || typeof fileName !== "string") {
      console.warn("파일명이 없음, 기본값 image/jpeg 사용");
      return "image/jpeg";
    }

    const parts = fileName.split(".");
    if (parts.length < 2) {
      console.warn("파일 확장자가 없음:", fileName);
      return "image/jpeg";
    }

    const ext = parts.pop().toLowerCase();
    const mimeTypes = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      bmp: "image/bmp",
    };
    return mimeTypes[ext] || "image/jpeg";
  };

  // 기존 이미지를 File 객체로 변환하는 함수
  const convertExistingImageToFile = async (imageUrl, fileName) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // fileName이 없으면 URL에서 추출 시도
      let finalFileName = fileName;
      if (!finalFileName) {
        const urlParts = imageUrl.split("/");
        finalFileName = urlParts[urlParts.length - 1] || "image.jpg";
      }

      // MIME 타입을 파일명 확장자로 결정
      const mimeType = getMimeTypeFromFileName(finalFileName);

      return new File([blob], finalFileName, { type: mimeType });
    } catch (err) {
      console.error("이미지 변환 실패:", err);
      return null;
    }
  };

  const handleSave = async () => {
    if (!formData.productNm.trim()) {
      alert("상품명을 입력해주세요.");
      return;
    }
    if (!formData.categoryIdx) {
      alert("카테고리를 선택해주세요.");
      return;
    }
    if (!formData.productPrice) {
      alert("가격을 입력해주세요.");
      return;
    }

    setLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append("productNm", formData.productNm);
      formDataObj.append("categoryIdx", formData.categoryIdx);
      formDataObj.append("productPrice", parseInt(formData.productPrice));
      formDataObj.append("productAmount", parseInt(formData.productAmount));
      formDataObj.append("productState", formData.productState);
      formDataObj.append("isAdult", formData.isAdult);
      formDataObj.append("isStock", formData.isStock);
      // blob URL을 [IMG_X] 플레이스홀더로 치환
      let descHtml = descEditorRef.current?.innerHTML || "";
      descImgFiles.forEach(({ blobUrl }, idx) => {
        descHtml = descHtml.split(blobUrl).join(`__PENDING_IMG_${idx}__`);
      });
      formDataObj.append("productDetailDescription", descHtml);
      descImgFiles.forEach(({ file }) => {
        formDataObj.append("productDescriptionImgs", file);
      });

      // 모든 이미지를 FormData에 추가 (기존 + 새 이미지)
      if (allImages && allImages.length > 0) {
        for (const img of allImages) {
          if (img.type === "new") {
            // 새로운 이미지는 그대로 추가
            formDataObj.append("productImgs", img.data);
          } else {
            // 기존 이미지는 fetch로 가져와서 File 객체로 변환
            const file = await convertExistingImageToFile(
              img.preview,
              img.name,
            );
            if (file) {
              formDataObj.append("productImgs", file);
            }
          }
        }
      }

      const response = await apiPutFormData(
        `/store/product/${product.productCode}`,
        formDataObj,
      );

      if (response.ok) {
        alert("상품이 수정되었습니다.");
        if (onSave) {
          onSave();
        }
      } else {
        const errorData = await response.json();
        alert(errorData.message || "상품 수정에 실패했습니다.");
      }
    } catch (err) {
      console.error("수정 실패:", err);
      alert("상품 수정 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 언마운트 시 미리보기 URL 해제
  useEffect(() => {
    return () => {
      allImages.forEach((img) => {
        if (img.type === "new") {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, [allImages]);

  return (
    <div className="productdetail-wrap">
      <div className="productdetail-modal">
        <div className="productdetail-header">상품 수정</div>

        {/* 상단 */}
        <div className="productdetail-top-content">
          <div className="productdetail-gallery-container">
            {/* 메인 이미지 */}
            <div className="productdetail-main-image-wrapper">
              {allImages.length > 0 ? (
                <img
                  src={allImages[selectedImageIndex].preview}
                  alt={product.productNm}
                  className="productdetail-main-image"
                />
              ) : (
                <div className="productdetail-image-placeholder">
                  이미지 없음
                </div>
              )}
            </div>

            {/* 서브 이미지 썸네일 갤러리 */}
            {allImages.length > 0 && (
              <div className="productdetail-thumbnail-container">
                {/* 왼쪽 화살표 */}
                <button
                  type="button"
                  onClick={() =>
                    setSelectedImageIndex((prev) =>
                      prev > 0 ? prev - 1 : allImages.length - 1,
                    )
                  }
                  className="productdetail-arrow-btn"
                >
                  ◀
                </button>

                {/* 썸네일 이미지들 - 고정 영역 (항상 3개 박스 표시) */}
                <div className="productdetail-thumbnail-list">
                  {[0, 1, 2].map((slotIndex) => (
                    <div
                      key={slotIndex}
                      className={`productdetail-thumbnail-slot ${
                        allImages[slotIndex] && selectedImageIndex === slotIndex
                          ? "active"
                          : ""
                      } ${allImages[slotIndex] ? "has-image" : "empty"}`}
                      onClick={() =>
                        allImages[slotIndex] && setSelectedImageIndex(slotIndex)
                      }
                    >
                      {allImages[slotIndex] ? (
                        <img
                          src={allImages[slotIndex].preview}
                          alt={`썸네일 ${slotIndex + 1}`}
                          className="productdetail-thumbnail-image"
                        />
                      ) : null}
                    </div>
                  ))}
                </div>

                {/* 오른쪽 화살표 */}
                <button
                  type="button"
                  onClick={() =>
                    setSelectedImageIndex((prev) =>
                      prev < allImages.length - 1 ? prev + 1 : 0,
                    )
                  }
                  className="productdetail-arrow-btn"
                >
                  ▶
                </button>
              </div>
            )}
          </div>

          <div className="productdetail-top-content-info">
            <div className="productdetail-top-content-info1">
              <input
                type="text"
                name="productNm"
                value={formData.productNm}
                onChange={handleChange}
                className="productmod-input"
                placeholder="상품명"
                disabled={!isEditable}
              />
            </div>

            <div className="productdetail-top-content-info3">
              카테고리:{" "}
              <select
                name="categoryIdx"
                value={formData.categoryIdx}
                onChange={handleChange}
                className="productmod-select"
                disabled={!isEditable}
              >
                <option value="">선택하세요</option>
                {categories.map((cat) => (
                  <option key={cat.categoryIdx} value={cat.categoryIdx}>
                    {cat.categoryNm}
                  </option>
                ))}
              </select>
            </div>

            <div className="productdetail-top-content-info4">
              판매가격:{" "}
              <input
                type="number"
                name="productPrice"
                value={formData.productPrice}
                onChange={handleChange}
                className="productmod-input"
                disabled={!isEditable}
              />
            </div>

            <div className="productdetail-top-content-info5">
              재고:{" "}
              <input
                type="number"
                name="productAmount"
                value={formData.productAmount}
                onChange={handleChange}
                className="productmod-input"
                disabled={!isEditable}
              />
            </div>

            <div className="productdetail-top-content-info6">
              판매상태:{" "}
              <span className="productmod-state-wrapper">
                <select
                  name="productState"
                  value={formData.productState}
                  onChange={handleChange}
                  className="productmod-select"
                  disabled={!isEditable || isStatusLocked}
                >
                  <option value="PENDING">대기</option>
                  <option value="ON_SALE">판매 중</option>
                  <option value="ENDED">판매 종료</option>
                </select>
                {isStatusLocked && (
                  <span className="productmod-tooltip">
                    할인 또는 네고 진행 중인 상품은 상태를 변경할 수 없습니다.
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* 중간 */}
        <div className="productdetail-middle-content">
          <div className="productdetail-middle-content-info1">
            <div className="productdetail-middle-content-info-title">
              할인 여부
            </div>
            <div className="productdetail-middle-content-info-detail">
              {product.isSale ? "예" : "아니오"}
            </div>
          </div>

          <div className="productdetail-middle-content-info2">
            <div className="productdetail-middle-content-info-title">
              네고 여부
            </div>
            <div className="productdetail-middle-content-info-detail">
              {product.isNego ? "예" : "아니오"}
            </div>
          </div>
          {/* 
          <div className="productdetail-middle-content-info3">
            <div className="productdetail-middle-content-info-title">상품구분</div>
            <div className="productdetail-middle-content-info-detail">
              <select
                name="isAdult"
                value={formData.isAdult}
                onChange={handleChange}
                className="productmod-select"
                disabled={!isEditable}
              >
                <option value={false}>일반</option>
                { <option value={true}>성인</option> }
              </select>
            </div>
          </div> */}

          <div className="productdetail-middle-content-info4">
            <div className="productdetail-middle-content-info-title">
              상품 구분
            </div>
            <div className="productdetail-middle-content-info-detail">
              <select
                name="isStock"
                value={formData.isStock}
                onChange={handleChange}
                className="productmod-select"
                disabled={!isEditable}
              >
                <option value={false}>일반</option>
                <option value={true}>서비스</option>
              </select>
            </div>
          </div>
        </div>

        <div className="productdetail-description">
          <div className="productdetail-description-title">상품 상세설명</div>
          <div className="productdetail-description-content">
            <div className="desc-editor-wrap">
              {isEditable && (
                <div className="desc-editor-toolbar">
                  <button
                    type="button"
                    className="desc-insert-image-btn"
                    onClick={() => {
                      const sel = window.getSelection();
                      if (
                        sel.rangeCount > 0 &&
                        descEditorRef.current.contains(sel.anchorNode)
                      ) {
                        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
                      }
                      descImgInputRef.current?.click();
                    }}
                  >
                    📷 이미지 삽입
                  </button>
                  <input
                    ref={descImgInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    style={{ display: "none" }}
                    onChange={handleDescImageInsert}
                  />
                </div>
              )}
              <div
                ref={descEditorRef}
                contentEditable={isEditable}
                className="desc-editor-content"
                suppressContentEditableWarning={true}
                data-placeholder="상품 상세설명 입력"
              />
            </div>
          </div>
        </div>

        <div className="fileinfo-middle-subtitle">상품 이미지 (최대 3개)</div>

        <div className="file-input-wrapper">
          <input
            type="file"
            id="productImage"
            accept=".jpg,.jpeg,.png"
            className="fileinfo-middle-file-input-hidden"
            onChange={handleProductImageChange}
            disabled={allImages.length >= 3 || !isEditable}
          />
          <label
            htmlFor="productImage"
            className={`file-input-label ${
              dragActive.product ? "drag-active" : ""
            } ${allImages.length >= 3 ? "full" : "can-upload"}`}
            onClick={handleFileLabelClick}
            onDragEnter={(e) =>
              allImages.length < 3 && handleDrag(e, "product")
            }
            onDragLeave={(e) =>
              allImages.length < 3 && handleDrag(e, "product")
            }
            onDragOver={(e) => allImages.length < 3 && handleDrag(e, "product")}
            onDrop={(e) => allImages.length < 3 && handleDrop(e, "product")}
            style={{ cursor: allImages.length >= 3 ? "pointer" : "pointer" }}
          >
            {/* 상태 배지 */}
            <div
              className={`productdetail-file-badge ${allImages.length < 3 ? "can-upload" : "full"}`}
            >
              <span>{allImages.length < 3 ? "➕" : "⚠️"}</span>
              <span>{allImages.length} / 3</span>
            </div>

            <div className="file-input-content">
              {allImages.length > 0 ? (
                <div className="file-preview-grid">
                  {allImages.map((img, index) => (
                    <div key={index} className="file-preview-item">
                      <button
                        type="button"
                        className="file-remove-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeImageFromList(index);
                        }}
                      >
                        ×
                      </button>
                      <img
                        src={img.preview}
                        alt="미리보기"
                        className="file-preview-image"
                      />
                      <span className="file-name-small">{img.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <span className="file-input-text">
                    등록할 상품 이미지를 올려주세요.(jpg, png){" "}
                  </span>
                  <span className="file-input-link">파일찾기</span>
                </>
              )}
            </div>
          </label>
        </div>

        <div className="productdetail-btn-area">
          {!isEditable && (
            <div className="productdetail-restricted-message">
              제재 상품입니다. 고객센터에 문의하세요.
            </div>
          )}
          <button
            className="productdetail-btn productdetail-btn-edit"
            onClick={handleSave}
            disabled={loading || !isEditable}
          >
            {loading ? "저장 중..." : "수정 완료"}
          </button>
          <button
            className="productdetail-btn productdetail-btn-close"
            onClick={onClose}
            disabled={loading}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductModDetail;
